import type { MenuItem } from '@/consts/navigation';
import type { TranslationKey } from '@/i18n/i18n';

import { getIcon } from './icons';
import type { MenuItemConfig } from './types';

interface TransformUrlOptions {
  url?: string;
  mode?: string;
  qs?: string;
}

const transformUrl = ({ url, mode, qs }: TransformUrlOptions): string | undefined => {
  if (!url) return undefined;

  let path: string;

  // Handle URLs with @/ prefix (legacy app routes)
  if (url.startsWith('@/')) {
    const cleanPath = url.substring(2);
    path = `/legacy/${cleanPath}`;
  } else {
    path = url.startsWith('/') ? url : `/${url}`;
  }

  // Append mode and QS as query parameters for legacy route resolution
  const params = new URLSearchParams();
  if (mode) params.set('mode', mode);
  if (qs) {
    // QS can be like "GroupCode=baltic" or "type=TCIN"
    const qsParts = new URLSearchParams(qs);
    qsParts.forEach((value, key) => {
      params.set(key, value);
    });
  }

  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const generateTranslationKey = (name: string, _parentKey?: string): TranslationKey => {
  // For legacy items, we'll just use the Name directly (without i18n)
  // This is a simplified approach - in production you might want to generate proper keys
  return name as TranslationKey;
};

export const transformMenuItem = (config: MenuItemConfig, _parentKey?: string): MenuItem => {
  const translationKey = generateTranslationKey(config.Name);
  const iconKey = config.Icon !== undefined && config.Icon !== '' ? config.Icon : config.Image;

  const menuItem: MenuItem = {
    text: translationKey,
    to: transformUrl({ url: config.Url, mode: config.Mode, qs: config.QS }),
    icon: iconKey ? getIcon(iconKey) : undefined,
    hidden: config.Hidden ?? false,
    code: config.Code as MenuItem['code']
  };

  // Transform children recursively
  if (config.Items && config.Items.length > 0) {
    menuItem.children = config.Items.map((item) => transformMenuItem(item, config.Code));
  }

  return menuItem;
};

/**
 * Flatten sub-group headers within a module.
 *
 * depth 0 (module's direct children): only flatten pure headers (no code AND no url).
 *   This preserves expandable parents like "Master Data", "Operations".
 * depth 1+ (within expanded parents): flatten ALL non-navigable items (no url),
 *   regardless of code. Removes sub-group labels like "VOYAGE TASKS", "ADMINISTRATION".
 */
const flattenSubGroups = (items: MenuItem[], depth = 0): MenuItem[] => {
  const result: MenuItem[] = [];
  for (const item of items) {
    const hasUrl = item.to !== undefined && item.to !== '';
    const hasCode = item.code !== undefined && (item.code as string) !== '';
    const hasChildren = item.children !== undefined && item.children.length > 0;

    const shouldFlatten = hasChildren && !hasUrl && (depth > 0 || !hasCode);

    if (shouldFlatten && item.children) {
      // Promote children to this level
      result.push(...flattenSubGroups(item.children, depth + 1));
    } else {
      // Keep item, recursively flatten its children
      const flattenedChildren = item.children ? flattenSubGroups(item.children, depth + 1) : undefined;
      result.push({ ...item, children: flattenedChildren });
    }
  }
  return result;
};

export const transformModuleConfig = (configs: MenuItemConfig[]): MenuItem[] => {
  return configs.map((config) => {
    const item = transformMenuItem(config);
    // Flatten sub-group headers within each module's children
    if (item.children) {
      return { ...item, children: flattenSubGroups(item.children) };
    }
    return item;
  });
};

interface RoleFilterOptions {
  userRoles: string[];
  item: MenuItemConfig;
  parentSecurity?: string[];
}

const parseRoles = (roleString?: string): string[] => {
  if (!roleString) return [];
  return roleString.split(';').filter(Boolean);
};

const hasAccess = (userRoles: string[], requiredRoles: string[]): boolean => {
  if (requiredRoles.length === 0) return true;
  return requiredRoles.some((role) => userRoles.includes(role));
};

export const filterByRole = ({ userRoles, item, parentSecurity }: RoleFilterOptions): boolean => {
  // Determine security rules - item-level overrides parent-level
  let requiredRoles: string[] = [];

  if (item.Security && item.Security.length > 0) {
    // Item has its own security rules
    const itemRoles = item.Security.flatMap((sec) => parseRoles(sec.Roles));
    requiredRoles = itemRoles;
  } else if (parentSecurity && parentSecurity.length > 0) {
    // Inherit from parent
    requiredRoles = parentSecurity;
  }

  // Check access
  return hasAccess(userRoles, requiredRoles);
};

export const filterMenuByRole = (configs: MenuItemConfig[], userRoles: string[]): MenuItemConfig[] => {
  return configs
    .map((config) => {
      // Get parent security for inheritance
      const parentSecurity = config.Security?.flatMap((sec) => parseRoles(sec.Roles));

      // Check if user has access to this item
      if (!filterByRole({ userRoles, item: config, parentSecurity })) {
        return null;
      }

      // Filter children recursively
      if (config.Items && config.Items.length > 0) {
        const filteredChildren = config.Items.filter((child) =>
          filterByRole({ userRoles, item: child, parentSecurity })
        );

        return {
          ...config,
          Items: filteredChildren
        };
      }

      return config;
    })
    .filter((item): item is MenuItemConfig => item !== null);
};
